import { useEffect, useMemo, useRef, useState } from "react";
import { Filter, RefreshCw, Search, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "react-hot-toast";
import { DsBadge, DsButton, DsIcon, DsInput } from "../../../design-system";
import { filterCatalogByCompanyModules } from "../../../utils/companyModules";
import { getScreenCatalogForRole } from "../../../rbac/catalog";
import { assignableEmployeeIds, isModuleGranted } from "./roleAccessUtils";
import { modulesGrantedCount, roleRisks } from "./roleRisks";

function userIdsMatchingFilter(filterData, users) {
  const userIds = new Set(assignableEmployeeIds(filterData).map(String));
  const empIds = new Set();
  const codes = new Set();
  for (const emp of filterData?.employeesData || []) {
    if (emp?.userId) userIds.add(String(emp.userId));
    if (emp?.user?.id) userIds.add(String(emp.user.id));
    if (emp?.id) empIds.add(String(emp.id));
    if (emp?.empCode) codes.add(String(emp.empCode).trim().toLowerCase());
  }
  return (users || []).filter((u) => {
    if (userIds.has(String(u.userId))) return true;
    if (u.employeeId != null && empIds.has(String(u.employeeId))) return true;
    if (u.empCode && codes.has(String(u.empCode).trim().toLowerCase())) return true;
    return false;
  });
}

const selectClass =
  "ds-input h-9 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))] disabled:cursor-not-allowed disabled:opacity-45";

function initials(name) {
  return (
    String(name || "?")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() || "")
      .join("") || "?"
  );
}

function formatLastActive(value) {
  if (!value) return "Never";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Never";
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return mins <= 1 ? "Just now" : `${mins} m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 45) return `${days} d ago`;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function statusTone(status) {
  if (status === "Invited") return "info";
  if (status === "Suspended" || status === "Inactive") return "danger";
  return "success";
}

function statusLabel(u) {
  if (u?.invited && u?.status === "Active" && !u?.lastLoginAt) return "Invited";
  return u?.status || "Active";
}

/**
 * Users tab: multi-select bulk assign / 2FA + side detail.
 */
export default function RoleUsersView({
  users,
  loading,
  error,
  onRetry,
  roles = [],
  accessByRole = {},
  canManageUsers = false,
  canAssignRole = false,
  busyUserId,
  bulkBusy = false,
  selectedUserId,
  onSelectUser,
  onChangeRole,
  onToggleActive,
  onToggle2fa,
  onRemoveUser,
  onBulkAssignRole,
  onBulkRequire2fa,
  filterResults = null,
  onOpenFilters,
}) {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [checked, setChecked] = useState(() => new Set());
  const [bulkRoleCode, setBulkRoleCode] = useState("");
  const filterAppliedRef = useRef("");

  const roleOptions = useMemo(() => {
    const names = new Set((users || []).map((u) => u.roleName).filter(Boolean));
    return ["All", ...[...names].sort((a, b) => a.localeCompare(b))];
  }, [users]);

  const activeRoles = useMemo(
    () => (roles || []).filter((r) => r.active !== false),
    [roles]
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (Array.isArray(users) ? users : []).filter((u) => {
      if (roleFilter !== "All" && u.roleName !== roleFilter) return false;
      if (!q) return true;
      return (
        String(u.name || "").toLowerCase().includes(q) ||
        String(u.email || "").toLowerCase().includes(q) ||
        String(u.empCode || "").toLowerCase().includes(q) ||
        String(u.roleName || "").toLowerCase().includes(q)
      );
    });
  }, [users, search, roleFilter]);

  useEffect(() => {
    setChecked((prev) => {
      const ids = new Set(visible.map((u) => String(u.userId)));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visible]);

  // Filters modal (shared) writes filterResults — select matching users for bulk assign.
  useEffect(() => {
    if (!filterResults?.employeesData) return;
    const sig = assignableEmployeeIds(filterResults).join(",");
    if (!sig || sig === filterAppliedRef.current) return;
    filterAppliedRef.current = sig;
    const matched = userIdsMatchingFilter(filterResults, users);
    if (!matched.length) {
      toast.error("No users matched that filter.");
      return;
    }
    setChecked(new Set(matched.map((u) => String(u.userId))));
    toast.success(`Selected ${matched.length} user${matched.length === 1 ? "" : "s"} from filter.`);
  }, [filterResults, users]);

  const selected =
    (users || []).find((u) => String(u.userId) === String(selectedUserId)) || visible[0] || null;

  useEffect(() => {
    if (!selected && visible[0]) onSelectUser?.(visible[0].userId);
  }, [selected, visible, onSelectUser]);

  const selectedRole =
    roles.find(
      (r) => String(r.code || "").toUpperCase() === String(selected?.roleCode || "").toUpperCase()
    ) ||
    roles.find((r) => r.name === selected?.roleName) ||
    null;
  const selectedCatalog = selectedRole
    ? filterCatalogByCompanyModules(getScreenCatalogForRole(selectedRole))
    : [];
  const selectedAccess = selectedRole ? accessByRole[selectedRole.id] : null;
  const risks = selectedRole ? roleRisks(selectedRole, selectedCatalog, selectedAccess) : [];
  const cov = modulesGrantedCount(selectedCatalog, selectedAccess);
  const busy =
    bulkBusy || (busyUserId != null && String(busyUserId) === String(selected?.userId));

  const allVisibleChecked =
    visible.length > 0 && visible.every((u) => checked.has(String(u.userId)));
  const checkedCount = checked.size;

  const toggleCheck = (userId, e) => {
    e?.stopPropagation?.();
    const id = String(userId);
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    if (allVisibleChecked) {
      setChecked((prev) => {
        const next = new Set(prev);
        visible.forEach((u) => next.delete(String(u.userId)));
        return next;
      });
    } else {
      setChecked((prev) => {
        const next = new Set(prev);
        visible.forEach((u) => next.add(String(u.userId)));
        return next;
      });
    }
  };

  const checkedUsers = useMemo(
    () => (users || []).filter((u) => checked.has(String(u.userId))),
    [users, checked]
  );

  const runBulkAssign = async () => {
    if (!bulkRoleCode || !checkedUsers.length || !onBulkAssignRole) return;
    await onBulkAssignRole(checkedUsers, bulkRoleCode);
    setChecked(new Set());
  };

  const runBulk2fa = async (require2fa) => {
    if (!checkedUsers.length || !onBulkRequire2fa) return;
    await onBulkRequire2fa(checkedUsers, require2fa);
    setChecked(new Set());
  };

  const gridCols = "grid-cols-[36px_minmax(0,2.2fr)_1.2fr_1fr_1fr_70px]";

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
      <section
        aria-label="Users"
        className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]"
      >
        <div className="flex shrink-0 flex-col gap-2 border-b border-[hsl(var(--ds-border))] p-3">
          <div className="flex min-w-0 flex-nowrap items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <DsIcon
                icon={Search}
                size="sm"
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
              />
              <DsInput
                type="search"
                placeholder="Search name or email"
                aria-label="Search users"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full min-w-0 pl-8"
              />
            </div>
            <select
              aria-label="Role filter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className={`${selectClass} w-auto max-w-[12rem] shrink-0`}
            >
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            {typeof onOpenFilters === "function" && (canAssignRole || canManageUsers) ? (
              <DsButton
                size="sm"
                variant="outline"
                className="shrink-0 whitespace-nowrap"
                onClick={onOpenFilters}
                disabled={busy}
              >
                <DsIcon icon={Filter} size="sm" />
                </DsButton>
            ) : null}
          </div>

          {(canAssignRole || canManageUsers) && checkedCount > 0 ? (
            <div className="flex flex-wrap items-center gap-2 rounded-[11px] bg-[hsl(var(--ds-muted))] px-3 py-2">
              <span className="text-xs font-medium text-[hsl(var(--ds-fg))]">
                {checkedCount} selected
              </span>
              {canAssignRole ? (
                <>
                  <select
                    aria-label="Bulk assign role"
                    value={bulkRoleCode}
                    onChange={(e) => setBulkRoleCode(e.target.value)}
                    disabled={busy}
                    className={`${selectClass} w-[180px]`}
                  >
                    <option value="">Assign role…</option>
                    {activeRoles.map((r) => (
                      <option key={r.id} value={String(r.code || "").toUpperCase()}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                  <DsButton
                    size="sm"
                    disabled={busy || !bulkRoleCode}
                    onClick={runBulkAssign}
                  >
                    Apply role
                  </DsButton>
                </>
              ) : null}
              {canManageUsers ? (
                <>
                  <DsButton
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => runBulk2fa(true)}
                  >
                    Require 2FA
                  </DsButton>
                  <DsButton
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => runBulk2fa(false)}
                  >
                    Clear 2FA
                  </DsButton>
                </>
              ) : null}
              <DsButton
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => setChecked(new Set())}
              >
                Clear selection
              </DsButton>
            </div>
          ) : null}
        </div>

        {error ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
            <p className="ds-body text-[hsl(var(--ds-danger))]">{error}</p>
            <DsButton size="sm" variant="outline" onClick={onRetry}>
              <DsIcon icon={RefreshCw} size="sm" />
              Retry
            </DsButton>
          </div>
        ) : loading ? (
          <div className="flex flex-1 items-center justify-center p-6">
            <p className="ds-body text-[hsl(var(--ds-muted-fg))]">Loading users…</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-1 items-center justify-center p-6 text-center">
            <p className="ds-body text-[hsl(var(--ds-muted-fg))]">
              {search || roleFilter !== "All" ? "No users match this filter." : "No users found."}
            </p>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto">
            <div
              className={`sticky top-0 z-[1] grid ${gridCols} gap-3 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-[18px] py-2.5 text-xs font-semibold text-[hsl(var(--ds-muted-fg))]`}
              role="row"
            >
              <span className="flex items-center">
                <input
                  type="checkbox"
                  aria-label="Select all visible users"
                  checked={allVisibleChecked}
                  onChange={toggleAllVisible}
                  className="h-4 w-4 accent-[hsl(var(--ds-primary))]"
                />
              </span>
              <span>User</span>
              <span>Role</span>
              <span>Status</span>
              <span>Last active</span>
              <span>2FA</span>
            </div>
            {visible.map((u) => {
              const sel = selected && String(u.userId) === String(selected.userId);
              const id = String(u.userId);
              const isChecked = checked.has(id);
              const label = statusLabel(u);
              return (
                <div
                  key={u.userId}
                  role="row"
                  aria-selected={sel}
                  onClick={() => onSelectUser?.(u.userId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelectUser?.(u.userId);
                    }
                  }}
                  tabIndex={0}
                  className={`grid w-full ${gridCols} cursor-pointer gap-3 border-b border-[hsl(var(--ds-border))] px-[18px] py-2.5 text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[hsl(var(--ds-ring))] ${
                    sel
                      ? "bg-[hsl(var(--ds-primary)/0.08)]"
                      : "bg-transparent hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  <span className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${u.name}`}
                      checked={isChecked}
                      onChange={(e) => toggleCheck(u.userId, e)}
                      className="h-4 w-4 accent-[hsl(var(--ds-primary))]"
                    />
                  </span>
                  <span className="flex min-w-0 items-center gap-2.5">
                    <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full bg-[hsl(var(--ds-emp,#5b4fd1))] text-[12px] font-semibold text-white">
                      {initials(u.name)}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-semibold text-[hsl(var(--ds-fg))]">
                        {u.name}
                      </span>
                      <span className="block truncate text-xs text-[hsl(var(--ds-muted-fg))]">
                        {u.email}
                      </span>
                    </span>
                  </span>
                  <span className="self-center truncate text-[hsl(var(--ds-fg))]">{u.roleName}</span>
                  <span className="self-center">
                    <DsBadge tone={statusTone(label)}>{label}</DsBadge>
                  </span>
                  <span className="self-center text-[hsl(var(--ds-muted-fg))]">
                    {formatLastActive(u.lastLoginAt)}
                  </span>
                  <span className="self-center">
                    {u.require2fa ? (
                      <DsBadge tone="success">On</DsBadge>
                    ) : (
                      <DsBadge tone="warning">Off</DsBadge>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <aside
        aria-label="User details"
        className="flex w-full shrink-0 flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)] lg:w-[340px] lg:max-h-none"
      >
        {!selected ? (
          <div className="flex flex-1 items-center justify-center p-6 text-center">
            <p className="ds-body text-[hsl(var(--ds-muted-fg))]">Select a user.</p>
          </div>
        ) : (
          <>
            <div className="flex shrink-0 items-center gap-2.5 border-b border-[hsl(var(--ds-border))] px-[18px] py-3.5">
              <span className="grid h-[30px] w-[30px] place-items-center rounded-full bg-[hsl(var(--ds-emp,#5b4fd1))] text-[12px] font-semibold text-white">
                {initials(selected.name)}
              </span>
              <div className="min-w-0">
                <p className="truncate font-semibold text-[hsl(var(--ds-fg))]">{selected.name}</p>
                <p className="truncate text-sm text-[hsl(var(--ds-muted-fg))]">{selected.email}</p>
              </div>
            </div>
            <div className="scroll-area grid min-h-0 flex-1 content-start gap-4 overflow-y-auto p-[18px]">
              <div className="grid gap-1.5">
                <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]" htmlFor="urm-user-role">
                  Role
                </label>
                <select
                  id="urm-user-role"
                  value={selected.roleCode || "EMPLOYEE"}
                  disabled={!canAssignRole || busy}
                  onChange={(e) => onChangeRole?.(selected, e.target.value)}
                  className={`${selectClass} w-full`}
                >
                  {activeRoles.map((r) => (
                    <option key={r.id} value={String(r.code || "").toUpperCase()}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-[hsl(var(--ds-fg))]">Account active</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={selected.status !== "Suspended" && selected.status !== "Inactive"}
                  aria-label="Account active"
                  disabled={!canManageUsers || busy}
                  onClick={() => onToggleActive?.(selected)}
                  className={`relative h-[22px] w-10 shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] disabled:cursor-not-allowed disabled:opacity-45 ${
                    selected.status !== "Suspended" && selected.status !== "Inactive"
                      ? "bg-[hsl(var(--ds-primary))]"
                      : "bg-[hsl(var(--ds-border))]"
                  }`}
                >
                  <span
                    className={`absolute top-[3px] h-4 w-4 rounded-full bg-white transition-[left] ${
                      selected.status !== "Suspended" && selected.status !== "Inactive"
                        ? "left-[21px]"
                        : "left-[3px]"
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-[hsl(var(--ds-fg))]">Require 2FA</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!selected.require2fa}
                  aria-label="Require 2FA"
                  disabled={!canManageUsers || busy}
                  onClick={() => onToggle2fa?.(selected)}
                  className={`relative h-[22px] w-10 shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] disabled:cursor-not-allowed disabled:opacity-45 ${
                    selected.require2fa ? "bg-[hsl(var(--ds-primary))]" : "bg-[hsl(var(--ds-border))]"
                  }`}
                >
                  <span
                    className={`absolute top-[3px] h-4 w-4 rounded-full bg-white transition-[left] ${
                      selected.require2fa ? "left-[21px]" : "left-[3px]"
                    }`}
                  />
                </button>
              </div>

              <div>
                <p className="mb-2 text-xs font-medium text-[hsl(var(--ds-muted-fg))]">
                  Effective access ({cov} of {selectedCatalog.length || 0} modules)
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedCatalog.map((m) => {
                    const on = isModuleGranted(m, selectedAccess?.[m.key]);
                    return (
                      <span
                        key={m.key}
                        className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          on
                            ? "bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))]"
                            : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))] opacity-55"
                        }`}
                      >
                        {m.label}
                      </span>
                    );
                  })}
                </div>
              </div>

              {risks.length ? (
                risks.map((msg) => (
                  <div
                    key={msg}
                    className="flex gap-2 rounded-[11px] bg-[hsl(var(--ds-danger)/0.1)] px-3 py-2.5 text-[13px] text-[hsl(var(--ds-danger))]"
                  >
                    <DsIcon icon={ShieldAlert} size="sm" className="mt-0.5 shrink-0" />
                    <span>{msg}</span>
                  </div>
                ))
              ) : (
                <div className="flex gap-2 rounded-[11px] bg-[hsl(var(--ds-success)/0.1)] px-3 py-2.5 text-[13px] text-[hsl(var(--ds-success))]">
                  <DsIcon icon={ShieldCheck} size="sm" className="mt-0.5 shrink-0" />
                  <span>No access risks found for this role.</span>
                </div>
              )}

              <DsButton
                variant="danger"
                disabled={!canManageUsers || busy}
                onClick={() => onRemoveUser?.(selected)}
              >
                {busy ? "Working…" : "Remove user"}
              </DsButton>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
